import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// The cancel UI shared by /refund and /cancel. Both portals look up the same
// billing payload by email, so they show the same two things: the growth plan
// subscription, and the auto-refill subscription — which bills on its own NMI
// subscription and does NOT stop when the plan is canceled.

const API = 'https://api.viralgpt.com';

const MONTHLY_PRICES = { starter: 29, basic: 59, pro: 199 };

const modalShellStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1050,
};

const modalBoxStyle = {
  background: '#fff',
  borderRadius: 8,
  width: '100%',
  maxWidth: 520,
  margin: '0 16px',
  maxHeight: '90vh',
  overflowY: 'auto',
};

// The annual buyer's alternative to canceling: drop to the monthly rate and get
// the difference back. Rendered inside both the refund modal and the cancel
// modal, so it lives here rather than in either page.
export function AnnualSwitchOffer({ data, setData, onDone }) {
  const L = useLanguage();
  const [switching, setSwitching] = useState(false);
  const sub = data?.active_subscription;

  if (!sub || sub.planInterval !== 'annual') return null;
  if (['canceled', 'inactive'].includes(sub.status)) return null;

  const monthlyPrice = MONTHLY_PRICES[sub.planName] || 0;
  const refundDiff = parseFloat((sub.price - monthlyPrice).toFixed(2));
  if (!(refundDiff > 0)) return null;

  const doSwitch = async () => {
    setSwitching(true);
    try {
      // The switch refunds against the original charge, so it needs that transaction.
      const originalTx = data.transactions?.find((tx) => tx.transaction_type === 'subcharge');
      if (!originalTx) { alert(L.refundPage.noOriginalTx); return; }
      const resp = await fetch(`${API}/refund/switch-to-monthly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid: data.user_id, transactionid: originalTx.transactionid }),
      });
      const body = await resp.json();
      if (resp.ok) {
        alert(L.refundPage.switchSuccess(body.refundAmount, body.newMonthlyPrice, new Date(body.nextChargeDate).toLocaleDateString()));
        // Keep the card on screen honest without a second lookup.
        setData((prev) => (prev?.active_subscription ? {
          ...prev,
          active_subscription: {
            ...prev.active_subscription,
            planInterval: 'monthly',
            price: body.newMonthlyPrice,
            nextChargeDate: body.nextChargeDate,
            status: 'active',
            endDate: null,
          },
        } : prev));
        if (onDone) onDone();
      } else {
        alert(body.error || L.refundPage.somethingWrong);
      }
    } catch {
      alert(L.refundPage.somethingWrong);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="alert alert-success">
      <p className="mb-1" style={{ fontWeight: 600 }}>{L.refundPage.switchTitle}</p>
      <p className="mb-2" style={{ fontSize: '0.9rem' }}>
        {L.refundPage.switchDesc(refundDiff, sub.price, monthlyPrice, sub.planName?.charAt(0).toUpperCase() + sub.planName?.slice(1))}
      </p>
      <div style={{ background: '#f0fdf4', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '0.85rem' }}>
        <div className="d-flex justify-content-between"><span>{L.refundPage.annualChargePaid}</span><strong>${sub.price}</strong></div>
        <div className="d-flex justify-content-between"><span>{L.refundPage.newMonthlyRate}</span><strong>{L.refundPage.perMo(monthlyPrice)}</strong></div>
        <hr style={{ margin: '6px 0' }} />
        <div className="d-flex justify-content-between" style={{ color: '#059669' }}><span>{L.refundPage.refundAmountLabel}</span><strong>${refundDiff}</strong></div>
      </div>
      <button type="button" className="btn btn-primary bg-instagram border-0 text-white" disabled={switching} onClick={doSwitch}>
        <i className="fas fa-exchange-alt me-2"></i>
        {switching ? L.refundPage.processing : L.refundPage.switchBtn(refundDiff)}
      </button>
    </div>
  );
}

export function SubscriptionCancelCards({ data, setData }) {
  const L = useLanguage();
  // `target` is what the modal is about to cancel: the plan, or one auto-refill row.
  const [target, setTarget] = useState(null);
  const [reasonType, setReasonType] = useState('');
  const [reason, setReason] = useState('');
  const [canceling, setCanceling] = useState(false);

  const sub = data?.active_subscription;
  const allRefills = data?.refill_subscriptions || [];
  const planCanceled = sub && ['canceled', 'inactive'].includes(sub.status);
  // Anything not already canceled can still bill — 'past_due' very much included,
  // since dunning will keep retrying it. Those are the rows that get a button.
  const liveRefills = allRefills.filter((r) => r.status !== 'canceled');
  // A customer with a long history can have several canceled rows; showing a
  // stack of them is noise, so they collapse to a single confirmation and only
  // when there is nothing left to cancel.
  const refills = liveRefills.length > 0 ? liveRefills : allRefills.slice(0, 1);
  const hasAnything = !!sub || refills.length > 0;

  const openModal = (t) => {
    setTarget(t);
    setReasonType('');
    setReason('');
  };

  const closeModal = () => {
    setTarget(null);
    setReasonType('');
    setReason('');
    setCanceling(false);
  };

  const submitCancel = async (e) => {
    e.preventDefault();
    if (!target) return;
    const finalReason = reasonType === 'other' ? reason.trim() : reasonType;
    if (!finalReason) {
      alert(reasonType === 'other' ? L.refundPage.cancelDetailsRequired : L.refundPage.cancelSelectReason);
      return;
    }
    setCanceling(true);
    try {
      const isPlan = target.kind === 'plan';
      const resp = await fetch(`${API}${isPlan ? '/refund/cancel-subscription' : '/refund/cancel-refill'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isPlan
          ? { userid: data.user_id, subscriptionid: sub.subscriptionid, reason: finalReason }
          : { userid: data.user_id, refillId: target.id, reason: finalReason }),
      });
      const body = await resp.json();
      if (resp.ok && body.success) {
        if (isPlan) {
          setData((prev) => ({
            ...prev,
            active_subscription: { ...prev.active_subscription, status: 'canceled', endDate: body.endDate },
          }));
          closeModal();
          alert(body.endDate
            ? L.refundPage.cancelSuccess(new Date(body.endDate).toLocaleDateString())
            : L.refundPage.cancelSuccessNoDate);
        } else {
          setData((prev) => ({
            ...prev,
            refill_subscriptions: (prev.refill_subscriptions || []).map((r) =>
              r.id === target.id ? { ...r, status: 'canceled' } : r),
          }));
          closeModal();
          alert(L.refundPage.autoRefillCanceled);
        }
      } else {
        alert(body.error || L.refundPage.somethingWrong);
      }
    } catch {
      alert(L.refundPage.somethingWrong);
    } finally {
      setCanceling(false);
    }
  };

  if (!hasAnything) return null;

  const isPlanTarget = target?.kind === 'plan';

  return (
    <>
      {sub && (() => {
        const endStr = sub.endDate
          ? new Date(sub.endDate).toLocaleDateString()
          : (sub.nextChargeDate ? new Date(sub.nextChargeDate).toLocaleDateString() : '');
        const planLabel = sub.planName ? sub.planName.charAt(0).toUpperCase() + sub.planName.slice(1) : '';
        const intervalLabel = sub.planInterval === 'annual' ? L.refundPage.intervalAnnual : L.refundPage.intervalMonthly;
        return (
          <div className="mt-4 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
            <h6 className="mb-3">{L.refundPage.subscriptionTitle}</h6>
            <div style={{ fontSize: '0.9rem' }}>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">{L.refundPage.subPlanLabel}</span>
                <strong>{planLabel} &middot; {intervalLabel}</strong>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">{L.refundPage.subPriceLabel}</span>
                <strong>${sub.price}</strong>
              </div>
              {!planCanceled && sub.nextChargeDate && (
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{L.refundPage.subNextChargeLabel}</span>
                  <strong>{new Date(sub.nextChargeDate).toLocaleDateString()}</strong>
                </div>
              )}
            </div>
            {planCanceled ? (
              <div className="alert alert-secondary mt-3 mb-0" style={{ fontSize: '0.88rem' }}>
                <i className="fas fa-check-circle me-2"></i>
                {endStr ? L.refundPage.alreadyCanceled(endStr) : L.refundPage.alreadyCanceledNoDate}
              </div>
            ) : (
              <button type="button" className="btn btn-outline-danger w-100 mt-3" onClick={() => openModal({ kind: 'plan' })}>
                {L.refundPage.cancelSubscription}
              </button>
            )}
          </div>
        );
      })()}

      {refills.map((r) => {
        const canceled = r.status === 'canceled';
        const nextBill = r.next_bill_at || r.first_bill_at;
        return (
          <div key={r.id} className="mt-4 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
            <h6 className="mb-3">{L.refundPage.refillSubTitle}</h6>
            <div style={{ fontSize: '0.9rem' }}>
              {r.username && (
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{L.refundPage.subProfileLabel}</span>
                  <strong>@{r.username}</strong>
                </div>
              )}
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">{L.refundPage.subPriceLabel}</span>
                <strong>{L.refundPage.refillSubPrice(r.amount, r.day_frequency)}</strong>
              </div>
              {!canceled && nextBill && (
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{L.refundPage.subNextChargeLabel}</span>
                  <strong>{new Date(nextBill).toLocaleDateString()}</strong>
                </div>
              )}
            </div>
            {canceled ? (
              <div className="alert alert-secondary mt-3 mb-0" style={{ fontSize: '0.88rem' }}>
                <i className="fas fa-check-circle me-2"></i>
                {L.refundPage.autoRefillAlreadyCanceled}
              </div>
            ) : (
              <button type="button" className="btn btn-outline-danger w-100 mt-3" onClick={() => openModal({ kind: 'refill', id: r.id })}>
                {L.refundPage.cancelAutoRefill}
              </button>
            )}
          </div>
        );
      })}

      {target && (
        <div
          style={modalShellStyle}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={modalBoxStyle}>
            <form onSubmit={submitCancel}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #dee2e6' }}>
                <h5 className="modal-title mb-0">
                  {isPlanTarget ? L.refundPage.cancelSubscription : L.refundPage.cancelAutoRefill}
                </h5>
              </div>
              <div style={{ padding: 20 }}>
                <p className="text-muted" style={{ fontSize: '0.9rem' }}>{L.refundPage.cancelWhy}</p>

                <div className="mb-3">
                  <label htmlFor="portal-cancel-reason-type" className="form-label">
                    {L.refundPage.cancelReasonLabel} <span className="text-danger">*</span>
                  </label>
                  <select
                    id="portal-cancel-reason-type"
                    className="form-select"
                    value={reasonType}
                    onChange={(e) => { setReasonType(e.target.value); setReason(''); }}
                    required
                  >
                    <option value="">{L.refundPage.selectReason}</option>
                    <option value="missing_followers">{L.refundPage.optMissingFollowers}</option>
                    <option value="price_too_high">{L.refundPage.cancelOptPriceTooHigh}</option>
                    <option value="not_using">{L.refundPage.cancelOptNotUsing}</option>
                    {isPlanTarget && sub?.planInterval === 'annual' && (
                      <option value="accidental_annual">{L.refundPage.optAccidentalAnnual}</option>
                    )}
                    <option value="other">{L.refundPage.optOther}</option>
                  </select>
                </div>

                {reasonType === 'missing_followers' && (
                  <div className="alert alert-info">
                    <p className="mb-2">{L.refundPage.refillFree}</p>
                    <a href="/refill" className="btn btn-primary bg-instagram border-0 text-white">
                      <i className="fas fa-redo me-2"></i>
                      {L.refundPage.goToRefill}
                    </a>
                  </div>
                )}

                {isPlanTarget && ['price_too_high', 'accidental_annual'].includes(reasonType) && (
                  <AnnualSwitchOffer data={data} setData={setData} onDone={closeModal} />
                )}

                {reasonType === 'other' && (
                  <div className="mb-3">
                    <label htmlFor="portal-cancel-reason" className="form-label">
                      {L.refundPage.detailsLabel} <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="portal-cancel-reason"
                      className="form-control"
                      placeholder={L.refundPage.cancelDetailsPlaceholder}
                      rows={4}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                    />
                  </div>
                )}

                {reasonType && (
                  <div className="alert alert-warning mb-0" style={{ fontSize: '0.88rem' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {isPlanTarget
                      ? (sub?.nextChargeDate
                        ? L.refundPage.cancelAccessNote(new Date(sub.nextChargeDate).toLocaleDateString())
                        : L.refundPage.cancelAccessNoteNoDate)
                      : L.refundPage.cancelAutoRefillNote}
                  </div>
                )}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #dee2e6', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={canceling}>
                  {L.refundPage.keepSubscription}
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={canceling || !reasonType || (reasonType === 'other' && !reason.trim())}
                >
                  {canceling ? L.refundPage.canceling : (isPlanTarget ? L.refundPage.confirmCancel : L.refundPage.cancelAutoRefill)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default SubscriptionCancelCards;
